import 'react-international-phone/style.css';
import './textinput.css';
import { Box, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { defaultCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone';
import debounce from 'lodash.debounce';
import { useSelector } from 'react-redux';
import { PhoneNumberUtil } from 'google-libphonenumber';

const languageToCountryMap = {
  EN: 'us', NL: 'nl', FR: 'fr', DE: 'de', ES: 'es', IT: 'it', ZH: 'cn',
};

const phoneUtil = PhoneNumberUtil.getInstance();

export const MuiPhoneComponent = ({
  showLabel, value, onChange, style = {}, error, helperText, label, name,
  borderClr = '#EBEBEB',
  ...restProps
}) => {
  const { defaultLanguage } = useSelector((store) => store?.addGuest);
  const defaultCountry = languageToCountryMap[defaultLanguage] || 'nl';
  const [validationError, setValidationError] = useState('');

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry,
    value,
    countries: defaultCountries,
  });


  const debouncedOnChange = useCallback(
    debounce((fieldName, val) => {
      onChange && onChange(fieldName, val);
    }, 300),
    [onChange]
  );

  // Sync external value changes (e.g. autofill, form reset)
  useEffect(() => {
    debouncedOnChange(name, inputValue);
    return () => { debouncedOnChange.cancel(); };
  }, [inputValue, name, debouncedOnChange]);

  const countryOptions = useMemo(() =>
    defaultCountries.map((c) => {
      const countryData = parseCountry(c);
      return (
        <MenuItem key={countryData.iso2} value={countryData.iso2}>
          <FlagImage iso2={countryData.iso2} style={{ marginRight: '8px' }} />
          <Typography marginRight="8px">{countryData.name}</Typography>
          <Typography color="gray">+{countryData.dialCode}</Typography>
        </MenuItem>
      );
    }), []
  );

  // Fix: delegate directly to usePhoneInput's handler so the library
  // manages formatting, country detection, and autofill correctly.
  // The previous implementation was stripping digits and calling a
  // wrong PhoneNumberUtil API which blocked autofill and short numbers.
  const handlePhoneInputChange = (e) => {
    handlePhoneValueChange(e);
  };

  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    setCountry(selectedCountry);
    // Reformat with new country dial code
    const dialCode = phoneUtil.getCountryCodeForRegion(selectedCountry.toUpperCase());
    const localNumber = inputValue.replace(/^\+\d+\s?/, '');
    onChange && onChange(name, `+${dialCode}${localNumber}`);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        id={name}
        // Use "off" to prevent browser autofill from injecting raw text
        // into a formatted phone field, but keep inputRef so the library
        // can still programmatically set the value on autofill events.
        autoComplete="tel"
        variant="outlined"
        placeholder="Phone number"
        value={inputValue}
        onChange={handlePhoneInputChange}
        name={name}
        type="tel"
        inputRef={inputRef}
        disabled={restProps.disabled}
        error={Boolean(error || validationError)}
        helperText={
          (error && helperText) ||
          (validationError ? validationError : '')
        }
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              style={{ marginRight: '2px', marginLeft: '-8px', width: '60px' }}
            >
              <Select
                MenuProps={{
                  style: { height: '400px', width: '360px', top: '20px', left: '-34px' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                }}
                sx={{
                  width: 'max-content',
                  fieldset: { display: 'none' },
                  '&.Mui-focused fieldset': { display: 'none !important' },
                  '.MuiSelect-select': { padding: '8px', paddingRight: '24px !important' },
                  svg: { right: 0 },
                }}
                value={country.iso2}
                onChange={handleCountryChange}
                renderValue={(val) => <FlagImage iso2={val} style={{ display: 'flex' }} />}
              >
                {countryOptions}
              </Select>
            </InputAdornment>
          ),
        }}
        sx={{
          width: '100%',
          '& .MuiFormHelperText-root': {
            color: 'text.secondary',
            '&.Mui-error': { color: 'text.secondary' },
          },
          '& .MuiOutlinedInput-root': {
            height: '40px',
            borderRadius: '8px',
            backgroundColor: '#FDFDFD',
            boxSizing: 'border-box',
            '& fieldset': {
              borderColor: borderClr,
              borderWidth: '1px !important',
            },
            '&:hover fieldset': {
              borderColor: borderClr,
              borderWidth: '1px !important',
            },
            '&.Mui-focused fieldset': {
              borderColor: borderClr,
              borderWidth: '1px !important',
            },
            '&.Mui-error fieldset': {
              borderColor: borderClr,
              borderWidth: '1px !important',
            },
            '&.Mui-error:hover fieldset': {
              borderColor: borderClr,
              borderWidth: '1px !important',
            },
            '&.Mui-focused.Mui-error fieldset': {
              borderColor: borderClr,
              borderWidth: '1px !important',
            },
            '& input': {
              height: '40px',
              boxSizing: 'border-box',
              padding: '0 14px 0 4px',
              outline: 'none',
              backgroundColor: 'transparent',
              // Allow browser autofill to paint correctly
              '&:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0px 1000px #FDFDFD inset !important',
                WebkitTextFillColor: 'inherit !important',
                borderRadius: '8px',
              },
              '&:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active': {
                WebkitBoxShadow: '0 0 0px 1000px #FDFDFD inset !important',
              },
            },
          },
          ...style,
        }}
      />
    </Box>
  );
};
